/**
 * Challenge type configurations
 */
export const challengeTypes = {
  "coverage-gap": {
    icon: "🧪",
    label: "Coverage Gap",
    color: "#33ff33"
  },
  "bug-hunt": {
    icon: "🔍",
    label: "Bug Hunt",
    color: "#00ffff"
  },
  "requirements": {
    icon: "📋",
    label: "Requirements Audit",
    color: "#ffb000"
  },
  "spot-the-bug": {
    icon: "🐛",
    label: "Spot the Bug",
    color: "#ff3333"
  }
};

/**
 * Difficulty multipliers for scoring
 */
export const difficultyMultipliers = {
  easy: 1,
  medium: 1.5,
  hard: 2
};

/**
 * All available challenges - will be randomized at game start
 */
const allChallenges = [
  // ============================================================================
  // EASY CHALLENGES (10 total)
  // ============================================================================
  {
    id: "login-blindspot",
    type: "coverage-gap",
    difficulty: "easy",
    title: "Login Blindspot",
    setup: "You're reviewing test coverage for a login system. The tests currently check valid credentials, invalid password, and empty fields.",
    testCases: [
      "✓ test_valid_login()",
      "✓ test_invalid_password()",
      "✓ test_empty_username()",
      "✓ test_empty_password()"
    ],
    question: "What critical security test is missing?",
    options: [
      "Test for SQL injection in username field",
      "Test for account lockout after failed attempts",
      "Test for password with special characters",
      "Test for username case sensitivity"
    ],
    correct: 1,
    explanation: "Account lockout is a critical security feature that prevents brute force attacks. Without testing this, an attacker could attempt unlimited password guesses.",
    takeaway: "Always test security boundaries like rate limiting and account lockout - they're your first defense against automated attacks."
  },
  {
    id: "off-by-one",
    type: "spot-the-bug",
    difficulty: "easy",
    title: "Off By One",
    setup: "A developer wrote this utility method to get the last N items from a list:",
    code: `public List<String> getLastN(List<String> items, int n) {
    int start = items.size() - n;
    return items.subList(start, items.size());
}`,
    question: "What happens when n is greater than the list size?",
    options: [
      "Returns an empty list",
      "Returns the full list",
      "Throws IndexOutOfBoundsException",
      "Returns null"
    ],
    correct: 2,
    explanation: "When n > items.size(), 'start' becomes negative. subList() with a negative index throws IndexOutOfBoundsException.",
    takeaway: "Always test boundary conditions: empty inputs, maximum values, and values that exceed expected ranges."
  },
  {
    id: "null-check-missing",
    type: "spot-the-bug",
    difficulty: "easy",
    title: "Null Pointer Trap",
    setup: "This method processes user input from a form:",
    code: `public String formatUserName(User user) {
    String firstName = user.getFirstName().trim();
    String lastName = user.getLastName().trim();
    return firstName + " " + lastName;
}`,
    question: "What's the most likely bug in this code?",
    options: [
      "String concatenation is inefficient",
      "No null check before calling trim()",
      "Should use StringBuilder",
      "Missing return type validation"
    ],
    correct: 1,
    explanation: "If getFirstName() or getLastName() returns null, calling trim() will throw a NullPointerException. Always validate before method chaining.",
    takeaway: "Null checks should happen before any method calls on potentially null objects - especially with user input."
  },
  {
    id: "api-status-codes",
    type: "coverage-gap",
    difficulty: "easy",
    title: "API Status Codes",
    setup: "You're testing a REST API endpoint for creating users. Current tests verify:",
    testCases: [
      "✓ 201 Created - valid user data",
      "✓ 400 Bad Request - missing required fields",
      "✓ 409 Conflict - duplicate email"
    ],
    question: "What common API response is NOT being tested?",
    options: [
      "200 OK",
      "401 Unauthorized / 403 Forbidden",
      "302 Redirect",
      "204 No Content"
    ],
    correct: 1,
    explanation: "Authentication and authorization (401/403) are critical for any API that creates resources. Without testing these, unauthorized users might be able to create accounts.",
    takeaway: "Always test authentication (401) and authorization (403) for protected endpoints - they're often overlooked but critical for security."
  },
  {
    id: "email-validation",
    type: "coverage-gap",
    difficulty: "easy",
    title: "Email Validation Gaps",
    setup: "The email validation tests currently include:",
    testCases: [
      "✓ test@example.com - valid",
      "✓ invalid-email - rejected",
      "✓ @nodomain.com - rejected",
      "✓ spaces in@email.com - rejected"
    ],
    question: "Which edge case is missing from these tests?",
    options: [
      "Email with numbers",
      "Email with subdomain",
      "Email longer than 254 characters",
      "Email with capital letters"
    ],
    correct: 2,
    explanation: "RFC 5321 limits email addresses to 254 characters. Many systems don't test this boundary, leading to database errors or truncation bugs.",
    takeaway: "Always test maximum length boundaries - they often reveal database constraints and buffer issues."
  },
  {
    id: "password-requirements",
    type: "requirements",
    difficulty: "easy",
    title: "Password Policy Gaps",
    setup: "The password requirements document states:",
    requirements: `Password Requirements:
- Minimum 8 characters
- At least one uppercase letter
- At least one number
- At least one special character`,
    question: "What critical requirement is missing?",
    options: [
      "Maximum length limit",
      "No spaces allowed",
      "Must start with a letter",
      "Cannot contain username"
    ],
    correct: 0,
    explanation: "Without a maximum length, users could submit extremely long passwords that cause denial of service or buffer overflow issues during hashing.",
    takeaway: "Always specify both minimum AND maximum constraints. Unbounded inputs are a security risk."
  },
  {
    id: "date-picker-test",
    type: "coverage-gap",
    difficulty: "easy",
    title: "Date Picker Testing",
    setup: "Testing a date picker for booking appointments. Current tests:",
    testCases: [
      "✓ Select today's date",
      "✓ Select future date",
      "✓ Verify date format displays correctly"
    ],
    question: "What obvious test case is missing?",
    options: [
      "Select a date in a different year",
      "Select a past date (should be disabled)",
      "Select February 29th",
      "Select a weekend"
    ],
    correct: 1,
    explanation: "For appointment booking, past dates should typically be disabled. Not testing this could allow users to book appointments in the past.",
    takeaway: "Think about what SHOULDN'T be allowed, not just what should work. Negative test cases catch real bugs."
  },
  {
    id: "shopping-cart-quantity",
    type: "spot-the-bug",
    difficulty: "easy",
    title: "Cart Quantity Bug",
    setup: "This function updates item quantity in a shopping cart:",
    code: `function updateQuantity(itemId, newQuantity) {
    const item = cart.find(i => i.id === itemId);
    item.quantity = newQuantity;
    calculateTotal();
}`,
    question: "What input would cause this function to fail?",
    options: [
      "Quantity of 1",
      "Quantity of 0",
      "Item ID that doesn't exist in cart",
      "Very large quantity"
    ],
    correct: 2,
    explanation: "If the itemId doesn't exist, find() returns undefined, and trying to set quantity on undefined throws 'Cannot set property of undefined'.",
    takeaway: "Always validate that lookup operations found something before operating on the result."
  },
  {
    id: "form-submission-test",
    type: "bug-hunt",
    difficulty: "easy",
    title: "Double Submit Defense",
    setup: "Testing a payment form submission. The test clicks Submit and verifies the order is created.",
    code: `test('submits payment successfully', () => {
    fillPaymentForm(validData);
    clickSubmit();
    expect(orderCreated()).toBe(true);
});`,
    question: "What critical user behavior is this test missing?",
    options: [
      "Filling the form too slowly",
      "Double-clicking the submit button",
      "Using keyboard to submit",
      "Submitting from mobile"
    ],
    correct: 1,
    explanation: "Users often double-click buttons, especially on slow connections. Without protection, this could create duplicate orders or charges.",
    takeaway: "Test for rapid repeated actions - double-clicks, double-taps, and button mashing are common user behaviors."
  },
  {
    id: "search-empty-results",
    type: "coverage-gap",
    difficulty: "easy",
    title: "Search Results Edge Case",
    setup: "Search functionality tests currently include:",
    testCases: [
      "✓ Search returns matching results",
      "✓ Search with special characters",
      "✓ Search is case-insensitive"
    ],
    question: "What user experience test is missing?",
    options: [
      "Search with very long query",
      "Search returns no results (empty state)",
      "Search with numbers",
      "Search while logged out"
    ],
    correct: 1,
    explanation: "The empty state is a critical UX scenario. Users need clear feedback when no results are found, not a blank screen or confusing error.",
    takeaway: "Always test empty states - they're one of the most common user experiences and often overlooked."
  },

  // ============================================================================
  // MEDIUM CHALLENGES (10 total)
  // ============================================================================
  {
    id: "phantom-test",
    type: "bug-hunt",
    difficulty: "medium",
    title: "Phantom Test",
    setup: "A colleague wrote this test for an email validator. It passes in CI.",
    code: `@Test
public void testEmailValidator() {
    EmailValidator validator = new EmailValidator();
    assertTrue(validator.isValid("test@example.com"));
}`,
    question: "Why is this test giving false confidence?",
    options: [
      "It doesn't test the error message",
      "It only tests the happy path - no invalid emails",
      "It should use assertEquals instead",
      "The test name is too generic"
    ],
    correct: 1,
    explanation: "This test only verifies that ONE valid email passes. A validator that returns true for everything would pass this test. You need negative test cases.",
    takeaway: "Every validator test needs both positive AND negative cases. A test that only checks 'valid' inputs proves nothing."
  },
  {
    id: "vague-ticket",
    type: "requirements",
    difficulty: "medium",
    title: "Vague Ticket",
    setup: "A product manager created this user story:",
    requirements: `User Story: Fast Checkout
As a returning customer
I want a quick checkout experience
So that I can complete purchases faster

Acceptance Criteria:
- Checkout should be fast
- Previously saved payment methods are available
- Order confirmation is displayed`,
    question: "What makes this story hard to test?",
    options: [
      "Missing user persona details",
      "'Fast' is not measurable - no performance criteria",
      "Too many acceptance criteria",
      "Missing error scenarios"
    ],
    correct: 1,
    explanation: "'Fast' is subjective. Without measurable criteria (e.g., 'checkout completes in under 3 seconds'), QA cannot objectively verify the requirement.",
    takeaway: "Push back on vague requirements. Every criterion should be measurable and testable."
  },
  {
    id: "file-upload-fury",
    type: "coverage-gap",
    difficulty: "medium",
    title: "File Upload Fury",
    setup: "Testing a profile picture upload feature. Current tests verify:",
    testCases: [
      "✓ Upload valid JPG under 5MB",
      "✓ Upload valid PNG under 5MB",
      "✓ Reject file over 5MB",
      "✓ Reject non-image file (.txt)"
    ],
    question: "What security-critical test is missing?",
    options: [
      "Test uploading a GIF",
      "Test uploading a file with no extension",
      "Test uploading a malicious file disguised as an image",
      "Test uploading from mobile device"
    ],
    correct: 2,
    explanation: "Attackers often disguise malicious files (like PHP scripts) with image extensions. Testing only by extension is insufficient - you need content-type validation.",
    takeaway: "File upload security requires testing both extension AND content validation. Never trust the file extension alone."
  },
  {
    id: "race-condition",
    type: "spot-the-bug",
    difficulty: "medium",
    title: "Race Condition",
    setup: "This service handles money transfers between accounts:",
    code: `public void transfer(Account from, Account to, BigDecimal amount) {
    if (from.getBalance().compareTo(amount) >= 0) {
        from.setBalance(from.getBalance().subtract(amount));
        to.setBalance(to.getBalance().add(amount));
        accountRepository.save(from);
        accountRepository.save(to);
    }
}`,
    question: "What concurrency issue exists in this code?",
    options: [
      "BigDecimal comparison is incorrect",
      "Missing null checks",
      "Not atomic - race condition between check and update",
      "Should use double instead of BigDecimal"
    ],
    correct: 2,
    explanation: "Between checking the balance and updating it, another thread could also pass the check. This allows overdrafts. Needs @Transactional with proper isolation.",
    takeaway: "Any check-then-act pattern in concurrent code needs synchronization or database-level locking."
  },
  {
    id: "timezone-trouble",
    type: "spot-the-bug",
    difficulty: "medium",
    title: "Timezone Trouble",
    setup: "This code checks if a promotion is currently active:",
    code: `public boolean isPromotionActive(Promotion promo) {
    Date now = new Date();
    return now.after(promo.getStartDate()) 
        && now.before(promo.getEndDate());
}`,
    question: "Why might this fail for some users?",
    options: [
      "Date is deprecated",
      "No timezone handling - user's local time vs server time",
      "Should use equals() instead of after()/before()",
      "Missing null checks"
    ],
    correct: 1,
    explanation: "Date uses the system timezone. A promotion ending at 'midnight' might end at different times for users in different timezones than intended.",
    takeaway: "Always be explicit about timezones. Store dates in UTC and convert for display based on user's timezone."
  },
  {
    id: "pagination-problems",
    type: "coverage-gap",
    difficulty: "medium",
    title: "Pagination Problems",
    setup: "Testing an API that returns paginated results. Current tests:",
    testCases: [
      "✓ First page returns correct items",
      "✓ Page size parameter works",
      "✓ Total count is correct"
    ],
    question: "What pagination edge case is missing?",
    options: [
      "Testing with page size of 1",
      "Requesting a page beyond the last page",
      "Testing with ascending sort",
      "Testing with filters applied"
    ],
    correct: 1,
    explanation: "What happens when someone requests page 999 of 10 pages? The API should return an empty result or appropriate error, not crash or return wrong data.",
    takeaway: "Always test boundary conditions in pagination: page 0, negative pages, and pages beyond the data."
  },
  {
    id: "mock-everything",
    type: "bug-hunt",
    difficulty: "medium",
    title: "Mock Overload",
    setup: "This integration test mocks the database:",
    code: `@Test
void testOrderCreation() {
    when(userRepository.findById(any())).thenReturn(mockUser);
    when(productRepository.findById(any())).thenReturn(mockProduct);
    when(orderRepository.save(any())).thenReturn(mockOrder);
    
    Order result = orderService.createOrder(userId, productId, quantity);
    
    assertNotNull(result);
}`,
    question: "What's wrong with this test approach?",
    options: [
      "Should use real assertions",
      "Too many mocks - not testing real integration",
      "Missing exception testing",
      "Should use @MockBean"
    ],
    correct: 1,
    explanation: "When everything is mocked, you're only testing that your mocks work together. Real database constraints, queries, and transactions are never tested.",
    takeaway: "Integration tests should test integration. Use mocks sparingly and test against real databases when possible."
  },
  {
    id: "session-timeout",
    type: "requirements",
    difficulty: "medium",
    title: "Session Timeout Spec",
    setup: "Security requirements for session management:",
    requirements: `Session Requirements:
- Sessions expire after 30 minutes of inactivity
- Users are redirected to login on expiration
- Session data is cleared on logout`,
    question: "What critical security requirement is missing?",
    options: [
      "Session ID regeneration after login",
      "Maximum concurrent sessions",
      "Session encryption",
      "All of the above are missing"
    ],
    correct: 3,
    explanation: "All three are critical: session fixation attacks (regeneration), account sharing concerns (concurrent limits), and data protection (encryption).",
    takeaway: "Session security needs defense in depth: regeneration, limits, encryption, and proper invalidation."
  },
  {
    id: "cache-invalidation",
    type: "spot-the-bug",
    difficulty: "medium",
    title: "Cache Confusion",
    setup: "This service caches user profiles for performance:",
    code: `@Cacheable("userProfiles")
public UserProfile getProfile(Long userId) {
    return userRepository.findById(userId)
        .orElseThrow(() -> new UserNotFoundException(userId));
}

public void updateProfile(Long userId, ProfileUpdateRequest request) {
    User user = userRepository.findById(userId).orElseThrow();
    user.setName(request.getName());
    user.setEmail(request.getEmail());
    userRepository.save(user);
}`,
    question: "What will users experience after updating their profile?",
    options: [
      "Changes appear immediately",
      "Changes never appear",
      "Stale data until cache expires",
      "Application crashes"
    ],
    correct: 2,
    explanation: "The update method doesn't invalidate the cache. Users will see their old profile data until the cache entry expires naturally.",
    takeaway: "Cache invalidation is one of the hardest problems in CS. Always pair @Cacheable with @CacheEvict on updates."
  },
  {
    id: "retry-logic-test",
    type: "bug-hunt",
    difficulty: "medium",
    title: "Retry Logic Flaw",
    setup: "Testing an API client with retry logic:",
    code: `@Test
void testRetryOnFailure() {
    when(apiClient.call()).thenThrow(new TimeoutException())
                          .thenReturn(successResponse);
    
    Response result = service.callWithRetry();
    
    assertEquals(successResponse, result);
    verify(apiClient, times(2)).call();
}`,
    question: "What real-world scenario is this test missing?",
    options: [
      "Testing with different exception types",
      "What happens after max retries are exhausted",
      "Testing the happy path",
      "Verifying log messages"
    ],
    correct: 1,
    explanation: "This test assumes retry eventually succeeds. In production, all retries might fail. You need to test the failure path when max retries are exhausted.",
    takeaway: "Retry tests must cover both eventual success AND complete failure after max attempts."
  },

  // ============================================================================
  // HARD CHALLENGES (10 total)
  // ============================================================================
  {
    id: "mock-mockery",
    type: "bug-hunt",
    difficulty: "hard",
    title: "Mock Mockery",
    setup: "This test suite has 100% code coverage but still misses bugs:",
    code: `@Test
void testPaymentProcessing() {
    when(paymentGateway.charge(any())).thenReturn(true);
    when(inventoryService.reserve(any())).thenReturn(true);
    when(emailService.send(any())).thenReturn(true);
    
    boolean result = orderService.processOrder(order);
    
    assertTrue(result);
}`,
    question: "What's the fundamental flaw in this testing approach?",
    options: [
      "Missing assertions on the order state",
      "All mocks return true - no failure scenarios tested",
      "Should use integration tests",
      "Test is too long"
    ],
    correct: 1,
    explanation: "Every mock returns success. What if payment fails? Inventory unavailable? Email bounces? The error handling paths are completely untested.",
    takeaway: "High coverage with all-happy-path mocks is worse than lower coverage with real failure testing."
  },
  {
    id: "missing-requirements",
    type: "requirements",
    difficulty: "hard",
    title: "Password Reset Holes",
    setup: "Password reset feature requirements:",
    requirements: `Password Reset Flow:
1. User enters email address
2. System sends reset link to email
3. User clicks link and enters new password
4. Password is updated and user is logged in`,
    question: "What critical security requirement is missing?",
    options: [
      "Email validation format",
      "Password strength requirements",
      "Reset link expiration time",
      "All of the above are important, but C is most critical"
    ],
    correct: 3,
    explanation: "Reset links without expiration are a major security risk. An old email could be compromised later, giving attackers permanent access.",
    takeaway: "Time-sensitive security tokens should ALWAYS have short expiration times (15-60 minutes max)."
  },
  {
    id: "sql-injection-test",
    type: "coverage-gap",
    difficulty: "hard",
    title: "Search Security",
    setup: "Testing a product search feature. Current tests verify:",
    testCases: [
      "✓ Search finds matching products",
      "✓ Search handles empty query",
      "✓ Search with special characters (!@#$%)",
      "✓ Search results are paginated"
    ],
    question: "What critical security test is missing?",
    options: [
      "Performance testing with large result sets",
      "Testing search ranking algorithm",
      "SQL injection testing ('; DROP TABLE products; --)",
      "Testing with unicode characters"
    ],
    correct: 2,
    explanation: "Search inputs that go to database queries are prime targets for SQL injection. Without explicit injection testing, you can't verify the app is safe.",
    takeaway: "Any user input that touches a database needs SQL injection testing. Use parameterized queries and verify with actual attack strings."
  },
  {
    id: "flaky-test",
    type: "bug-hunt",
    difficulty: "hard",
    title: "Flaky Test",
    setup: "This test sometimes passes, sometimes fails in CI:",
    code: `@Test
void testGetRecentOrders() {
    orderService.createOrder(testUser, product1);
    orderService.createOrder(testUser, product2);
    
    List<Order> recent = orderService.getRecentOrders(testUser);
    
    assertEquals(product1.getId(), recent.get(0).getProductId());
    assertEquals(product2.getId(), recent.get(1).getProductId());
}`,
    question: "Why does this test fail intermittently?",
    options: [
      "Database connection issues",
      "Test data from previous runs",
      "Assumes specific ordering that's not guaranteed",
      "Memory leak in test setup"
    ],
    correct: 2,
    explanation: "The test assumes orders return in creation order, but 'recent' might mean different ordering (by ID, timestamp, etc.). Without ORDER BY guarantees, results vary.",
    takeaway: "Never assume database ordering. Either explicitly sort or test in an order-independent way (containsAll instead of indexed assertions)."
  },
  {
    id: "xss-vulnerability",
    type: "coverage-gap",
    difficulty: "hard",
    title: "Comment System Security",
    setup: "Testing a blog comment feature. Current tests:",
    testCases: [
      "✓ Post comment successfully",
      "✓ Comment appears on page",
      "✓ Edit own comment",
      "✓ Delete own comment"
    ],
    question: "What critical security test is missing?",
    options: [
      "Testing comment length limits",
      "Testing with HTML/JavaScript in comment (XSS)",
      "Testing comment threading",
      "Testing comment notifications"
    ],
    correct: 1,
    explanation: "User-generated content displayed on pages must be tested for XSS. Without sanitization, attackers can inject scripts that steal user sessions.",
    takeaway: "Any user input displayed back on the page needs XSS testing. Test with <script>alert('xss')</script> and verify it's escaped."
  },
  {
    id: "api-versioning",
    type: "requirements",
    difficulty: "hard",
    title: "API Contract Gaps",
    setup: "API documentation for a versioned endpoint:",
    requirements: `GET /api/v2/users/{id}
Response:
{
  "id": number,
  "name": string,
  "email": string,
  "createdAt": string
}

Note: v1 is deprecated but still supported`,
    question: "What's missing from this API contract?",
    options: [
      "Request headers documentation",
      "Error response format and status codes",
      "Rate limiting information",
      "All of the above"
    ],
    correct: 3,
    explanation: "A complete API contract needs error formats (what does 404 look like?), required headers (auth?), and rate limits. Without these, clients can't handle failures properly.",
    takeaway: "API documentation must cover the unhappy paths: errors, limits, required headers, and deprecation timelines."
  },
  {
    id: "memory-leak-test",
    type: "bug-hunt",
    difficulty: "hard",
    title: "Resource Leak",
    setup: "This code reads configuration from files:",
    code: `public Properties loadConfig(String filename) {
    try {
        FileInputStream fis = new FileInputStream(filename);
        Properties props = new Properties();
        props.load(fis);
        return props;
    } catch (IOException e) {
        throw new ConfigException("Failed to load: " + filename, e);
    }
}`,
    question: "What resource management issue exists?",
    options: [
      "Should use Properties.loadFromXML()",
      "FileInputStream is never closed (resource leak)",
      "Exception handling is too broad",
      "Should return Optional<Properties>"
    ],
    correct: 1,
    explanation: "The FileInputStream is never closed, even if load() succeeds. Over time, this exhausts file handles. Use try-with-resources.",
    takeaway: "Any resource that needs closing (streams, connections, etc.) should use try-with-resources or explicit finally blocks."
  },
  {
    id: "distributed-transaction",
    type: "spot-the-bug",
    difficulty: "hard",
    title: "Distributed Failure",
    setup: "Microservice order processing:",
    code: `public void processOrder(Order order) {
    paymentService.charge(order.getTotal());      // Service A
    inventoryService.reserve(order.getItems());   // Service B
    shippingService.schedule(order.getAddress()); // Service C
    orderRepository.save(order);
}`,
    question: "What happens if Service C fails after A and B succeed?",
    options: [
      "Order is saved without shipping",
      "All services automatically rollback",
      "Payment charged, inventory reserved, but no shipping scheduled",
      "Exception is logged and ignored"
    ],
    correct: 2,
    explanation: "Without distributed transaction handling (saga pattern), partial failures leave inconsistent state: money taken, items reserved, but order incomplete.",
    takeaway: "Distributed systems need compensation logic (sagas) or idempotent retry. Local transactions don't span service boundaries."
  },
  {
    id: "auth-bypass",
    type: "coverage-gap",
    difficulty: "hard",
    title: "Authorization Bypass",
    setup: "Testing an admin dashboard. Current security tests:",
    testCases: [
      "✓ Admin can access dashboard",
      "✓ Regular user is redirected to login",
      "✓ Unauthenticated user is redirected to login"
    ],
    question: "What authorization test is missing?",
    options: [
      "Testing session timeout",
      "Testing CSRF protection",
      "Testing direct URL access to admin functions by regular user (IDOR)",
      "Testing password complexity"
    ],
    correct: 2,
    explanation: "IDOR (Insecure Direct Object Reference): Can a logged-in regular user access /admin/users/delete/123 directly? Testing only the dashboard link misses direct URL attacks.",
    takeaway: "Authorization must be tested at the endpoint level, not just UI level. Users can craft URLs directly."
  },
  {
    id: "concurrent-update",
    type: "spot-the-bug",
    difficulty: "hard",
    title: "Lost Update Problem",
    setup: "Two users editing the same document:",
    code: `// User A and User B both load document at version 1
// User A saves: "Hello World"
// User B saves: "Goodbye World"
// Final result: "Goodbye World" (User A's changes lost)

public void saveDocument(Long docId, String content) {
    Document doc = documentRepository.findById(docId);
    doc.setContent(content);
    doc.setUpdatedAt(Instant.now());
    documentRepository.save(doc);
}`,
    question: "How should this be fixed?",
    options: [
      "Use synchronized keyword",
      "Implement optimistic locking with version field",
      "Use database-level locks on read",
      "Save to a queue instead"
    ],
    correct: 1,
    explanation: "Optimistic locking (version field) detects conflicts: if version changed since load, reject the save and notify the user to merge changes.",
    takeaway: "Concurrent updates need either optimistic locking (version checks) or pessimistic locking (database locks). Choose based on conflict frequency."
  }
];

/**
 * Shuffle array
 */
function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Get a randomized set of challenges for a new game
 * Ensures balanced difficulty: mix of easy, medium, and hard
 * @param {number} count - Total number of challenges to return (default 10)
 * @returns {Array} Shuffled array of challenges
 */
export function getRandomizedChallenges(count = 10) {
  // Separate by difficulty
  const easy = allChallenges.filter(c => c.difficulty === 'easy');
  const medium = allChallenges.filter(c => c.difficulty === 'medium');
  const hard = allChallenges.filter(c => c.difficulty === 'hard');
  
  // Shuffle each difficulty pool
  const shuffledEasy = shuffleArray(easy);
  const shuffledMedium = shuffleArray(medium);
  const shuffledHard = shuffleArray(hard);
  
  // For 10 challenges: 3 easy, 4 medium, 3 hard (balanced difficulty curve)
  const easyCount = Math.floor(count * 0.3);
  const hardCount = Math.floor(count * 0.3);
  const mediumCount = count - easyCount - hardCount;
  
  const selected = [
    ...shuffledEasy.slice(0, easyCount),
    ...shuffledMedium.slice(0, mediumCount),
    ...shuffledHard.slice(0, hardCount)
  ];
  
  // Shuffle the final selection so difficulty isn't predictable
  return shuffleArray(selected);
}

/**
 * Legacy export for backwards compatibility
 * Returns a new randomized set each time
 */
export const challenges = getRandomizedChallenges(10);